import React, { useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import {
  BodyText,
  Button,
  DisplayText,
  IllusDressing_1,
  Image,
  theme,
} from '@sg/garnish';

import { PageWrapper } from '@order/components';
import { useLocalizationContext } from '@order/Localization';

// ────────────────────────────────────────────────────────────────────────────────

export const NotFoundView = (props: NotFoundViewProps) => {
  const { description, btnLabel, withoutFooter, onBtnPress, onMount } = props;

  const onMountCallbackRef = useRef<NotFoundViewProps['onMount']>(onMount);

  // eslint-disable-next-line functional/immutable-data
  onMountCallbackRef.current = onMount;

  const { t } = useLocalizationContext();
  const { navigate } = useNavigation();

  // ─── Helpers ─────────────────────────────────────────────────────

  const handlePressHome = React.useCallback(() => {
    navigate('MainTabs', {
      screen: 'HomeTab',
      params: { screen: 'Home' },
    });
  }, [navigate]);

  // ─── Effects ─────────────────────────────────────────────────────

  // triggers the `onMount` callback on component mount (if provided)
  useEffect(() => {
    const { current: onMountCallback } = onMountCallbackRef;

    if (onMountCallback) onMountCallback();
  }, []);

  // ─────────────────────────────────────────────────────────────────

  return (
    <PageWrapper
      contentContainerStyle={styles.container}
      safeAreaEdges={['top']}
      withoutFooter={withoutFooter}
    >
      <View testID="not-found-view" style={styles.wrapper}>
        <Image
          source={IllusDressing_1}
          style={styles.img}
          accessibilityIgnoresInvertColors
          resizeMode="contain"
        />

        <DisplayText
          size={2}
          style={styles.heading}
          accessibilityRole="header"
          accessibilityLevel={1}
        >
          {t('not-found-view.heading')}
        </DisplayText>

        <BodyText style={styles.text}>
          {description ?? t('not-found-view.text')}
        </BodyText>

        <Button
          testID="not-found-view.btn"
          palette="primary"
          size="large"
          style={styles.btn}
          onPress={onBtnPress ?? handlePressHome}
        >
          {btnLabel ?? t('not-found-view.return-home')}
        </Button>
      </View>
    </PageWrapper>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: theme.colors.OATMEAL,
  },
  wrapper: {
    flex: 1,
    paddingVertical: theme.spacing.medium,
    paddingHorizontal: theme.spacing.large,
    alignItems: 'center',
    justifyContent: 'center',
  },
  img: {
    width: 200,
    height: 200,
  },
  heading: {
    marginVertical: theme.spacing.medium,
    textAlign: 'center',
  },
  text: {
    textAlign: 'center',
  },
  btn: {
    marginTop: theme.spacing.xlarge,
    minWidth: 193,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────────

type NotFoundViewProps = {
  readonly description?: string;
  readonly btnLabel?: string;
  readonly withoutFooter?: boolean;
  readonly onBtnPress?: () => void;
  readonly onMount?: () => void;
};
